import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  buildHrHome, yearMonthOf, type HrHomeStats, monthlyFlow, leftInMonth, recentEvents,
} from '../../lib/hr-home';
import { listEmployees, type EmployeeRowDto } from '../../api/hr-employee';
// 화면 머리 — 근태 · 메일과 같은 정본 A3 (2026-09-08).
import { PageHeader } from '../../components/shared/PageHeader';
import { listLeaveQuotas } from '../../api/hr-admin';
import {
  pendingRows, pendingHeadline, countText, unfinishedProfiles,
  type PendingInput, type PendingRow,
} from '../../lib/hr-pending';
import { fetchAwards } from '../../api/hr-awards';
import { fetchTrainings } from '../../api/hr-trainings';
import { listProfileRequests } from '../../api/hr-profile-requests';
import { listAssignments, type AssignmentDto } from '../../api/hr-assignment';
import { fetchRounds } from '../../api/hr-evaluations';
// 오늘 근태 — 전사 한 장(시안 A1). 팀 근태는 한 번에 한 팀이라 도넛을 못 그렸다.
import { fetchAttendanceToday, type AttendanceTodayDto } from '../../api/hr-attendance-today';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/**
 * 인사 홈 — 인사 메뉴의 착지 화면.
 *
 * 숫자는 **인사 담당자가 이미 부르는 두 목록**에서 센다(직원 명부 · 연차 현황) —
 * 새 API 도, 새 표도 없다. 집계는 `lib/hr-home.ts` 한 곳에서만 한다.
 *
 * ★ 못 읽으면 **화면을 접는다.** 옛 서버(라우트 없음)·권한 부족 어느 쪽이든 지표만 빠지고
 *   트리는 그대로 남는다 — 홈이 통째로 죽어도 갈 곳은 있다(팀 근태 API 가 쓰는 것과 같은 태도).
 * ★★ **«처리 대기» 도 그 태도를 따른다** — 지표(`stats`) 블록 **밖**에 둔다. 안에 두면
 *    직원 명부를 못 읽는 순간 처리 대기까지 함께 사라진다(테스트가 이것을 잡았다).
 *    조각마다 `allSettled` 라, 다섯 응답 중 하나가 죽어도 **그 줄만 «—»** 가 된다.
 * ★★ **오늘 근태는 전사 한 장을 따로 받는다**(2026-08-27, 시안 A1). 팀 근태 API 가 **한 번에
 *    한 팀**이라 예전에는 홈이 이 값을 아예 못 그렸다 — `/hr/attendance/today` 가 그 자리다.
 *    **숫자만** 온다(이름도 시각도 없다). 못 읽으면 그 상자만 빠진다.
 */
export function HrHomePage() {
  const [stats, setStats] = useState<HrHomeStats | null>(null);
  /** 명부 원본 — 월별 입사를 세려면 이번 달만으로는 안 된다(한 해치가 필요하다). */
  const [employees, setEmployees] = useState<readonly EmployeeRowDto[]>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  /**
   * ★ 조각들의 «원본» 을 들고 있다가 **그릴 때 명부와 합쳐** 줄을 만든다 (2026-08-28).
   *   「인사 정보 없는 계정」은 명부에서 세는데, 명부는 다른 조각에서 온다 —
   *   줄을 미리 만들어 두면 명부가 늦게 와도 그 줄만 영영 «—» 로 남는다.
   */
  const [pendingInput, setPendingInput] = useState<PendingInput | null>(null);
  /** 오늘 근태 — 못 읽으면(옛 서버 404 · 전사를 못 보는 사람 403) 그 상자만 빠진다. */
  const [today, setToday] = useState<AttendanceTodayDto | null>(null);
  /** 적용된 발령 — 최근 인사 이벤트와 월별 퇴사를 여기서 센다(새 API 0). */
  const [assignments, setAssignments] = useState<readonly AssignmentDto[] | null>(null);
  const yearMonth = yearMonthOf(new Date());
  const year = Number(yearMonth.slice(0, 4));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [emp, quota] = await Promise.all([listEmployees(), listLeaveQuotas(year)]);
        if (!alive) return;
        setEmployees(emp.rows);
        setStats(buildHrHome(emp.rows, quota.rows, yearMonth));
        setState('ok');
      } catch {
        if (alive) setState('folded');
      }
    })();
    return () => { alive = false; };
  }, [year, yearMonth]);

  /**
   * «처리 대기» — 담당자가 이미 부르는 응답들의 `summary` 를 모은다(새 표·새 라우트 0).
   *
   * ★★ **`allSettled` 다.** 하나가 404(옛 서버)여도 나머지는 뜬다 — 위 머리말의 태도를
   *    조각 단위로 내린 것이다. 못 읽은 조각은 **`null`(«—»)** 이지 `0` 이 아니다.
   */
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const r = await fetchAttendanceToday(); if (alive) setToday(r); } catch { /* 그 상자만 빠진다 */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [req, tr, aw, asg, rd] = await Promise.allSettled([
        listProfileRequests({ status: 'PENDING' }),
        fetchTrainings(),
        fetchAwards(),
        listAssignments(),
        fetchRounds(),
      ]);
      if (!alive) return;
      const ok = <T,>(r: PromiseSettledResult<T>): T | null => (r.status === 'fulfilled' ? r.value : null);
      const rq = ok(req); const tt = ok(tr); const aa = ok(aw); const ag = ok(asg); const rr = ok(rd);
      setAssignments(ag ? ag.assignments : null);
      const input: PendingInput = {
        requests: {
          pending: rq ? rq.summary.pending : null,
          needsAssignment: rq ? rq.summary.pendingNeedsAssignment : null,
        },
        assignmentsOverdue: ag ? ag.overdue : null,
        courseNotCompleted: tt ? tt.summary.notCompleted : null,
        credentials: {
          expiringSoon: tt ? tt.summary.expiringSoon : null,
          expired: tt ? tt.summary.expired : null,
        },
        awardsExpiringSoon: aa ? aa.summary.expiringSoon : null,
        activeRounds: rr ? rr.rounds.filter((x) => x.status === 'ACTIVE').length : null,
        // 명부는 다른 조각에서 온다 — 여기서는 «아직 모름» 으로 두고 그릴 때 채운다.
        unfinished: unfinishedProfiles(null),
      };
      setPendingInput(input);
    })();
    return () => { alive = false; };
  }, []);

  /**
   * 적용된 «퇴직» 발령 — 월별 퇴사와 «이번 달 퇴사» 가 이것을 센다.
   * ★ 상태만 «퇴사» 로 바뀐 사람은 날짜가 없어 어느 달에 세울지 알 수 없다(인사 통계와 같은 규칙).
   */
  /**
   * 그릴 줄 — 조각 원본 + 명부. 명부를 못 읽었으면(`folded`) 그 줄만 «—» 다.
   * ★ 정렬은 `pendingRows` 안에 있다 — 여기서 줄을 덧붙이면 그 순서가 깨진다.
   */
  const pending: PendingRow[] | null = pendingInput
    ? pendingRows({
      ...pendingInput,
      unfinished: unfinishedProfiles(state === 'ok' ? employees : null),
    })
    : null;

  const resigns = (assignments ?? [])
    .filter((a) => a.kind === 'RESIGN' && a.status === 'APPLIED')
    .map((a) => ({ userId: a.userId, date: a.effectiveDate }));

  const flow = monthlyFlow(employees, resigns, year);
  const flowMax = Math.max(1, ...flow.flatMap((m) => [m.hired, m.left]));
  const events = recentEvents(assignments ?? [], stats?.hiredThisMonth ?? []);
  const leftThisMonth = leftInMonth(resigns, yearMonth);
  /** 근태 이상 — 미퇴근 + 지각. 못 읽었으면 «—» 다(0 이 아니다). */
  const anomalies = today ? today.openRecords + today.late : null;
  const pendingTotal = pending
    ? pending.reduce((n, r) => n + (typeof r.count === 'number' ? r.count : 0), 0)
    : null;

  return (
    <div className="hrpage">
      {/*
        ★★★ 화면 머리 — **정본 `PageHeader`(A3)** (2026-09-08 사용자 지적).
           손으로 짠 `<h1>` + `<p>` 라 **경로 · 도움말**이 없었고, 경로를 «글자»로
           설명문 앞에 적고 있었다 — 경로는 `breadcrumb` 이 그린다.
        ★★ **경로의 마지막은 «지금 화면»**(A3 · 2026-09-08 사용자 지시로 메일에서 못 박은 규칙).
           처음엔 옛 글자를 그대로 옮겨 「업무 › 인사」로 뒀는데, 그러면 **인사만 다른 모양**이 된다.
        ★ 제목은 **메뉴 이름과 같아야 한다** — 이 주소는 보는 사람에 따라 갈리므로
          메뉴도 둘(«인사 홈» · «내 인사 홈»)이고, 제목도 그 둘을 그대로 쓴다.
      */}
      <PageHeader
        className="hrpage__head"
        breadcrumb={crumbsFor('/erp/hr')}
        scope={scopeFor('/erp/hr')}
        title="인사 홈"
        description={`${year}년 기준 · 직원 · 인사기록 · 조직과 권한을 한 곳에서 봅니다`}
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p><b>직원</b>은 재직 중인 사람만 셉니다 — 퇴사자는 「직원 관리」에서 따로 봅니다.</p>
              <p><b>연차</b>는 회사가 정한 기준(입사일 · 회계연도)으로 셉니다. 기준은 「근태 &gt; 연차 정책」이 정합니다.</p>
              <p>여기 숫자는 <b>지금 이 순간</b>의 값입니다 — 과거 시점은 각 화면에서 기간을 골라 봅니다.</p>
            </>
          ),
        }}
      />

      {state === 'loading' && <div className="hrpage__note">불러오는 중…</div>}

      {state === 'ok' && stats && (
        <>
          <div className="hrpage__kpis">
            <div className="hrpage__kpi">
              <span className="hrpage__kpil">재직 인원</span>
              <span className="hrpage__kpiv">{stats.active}<small>명</small></span>
              <span className="hrpage__kpid">
                계약직 {stats.contract}명{stats.onLeave > 0 ? ` · 휴직 ${stats.onLeave}명` : ''}
              </span>
            </div>
            <div className="hrpage__kpi">
              <span className="hrpage__kpil">이번 달 입사</span>
              <span className="hrpage__kpiv">{stats.hiredThisMonth.length}<small>명</small></span>
              <span className="hrpage__kpid">{yearMonth.replace('-', '년 ')}월</span>
            </div>
            {/* ★ 시안 A1 의 다섯 칸 — 사람 수(재직·입사·퇴사)와 **내가 지금 해야 할 것**(대기·이상).
                연차 숫자는 아래 «팀별 연차 소진율» 과 «손봐야 할 것» 이 이미 들고 있다. */}
            <div className="hrpage__kpi">
              <span className="hrpage__kpil">이번 달 퇴사</span>
              <span className="hrpage__kpiv">{leftThisMonth}<small>명</small></span>
              <span className="hrpage__kpid">적용된 퇴직 발령</span>
            </div>
            <div className={`hrpage__kpi${(pendingTotal ?? 0) > 0 ? ' is-warn' : ''}`}>
              <span className="hrpage__kpil">처리 대기</span>
              <span className="hrpage__kpiv">
                {pendingTotal === null ? '—' : pendingTotal}{pendingTotal !== null && <small>건</small>}
              </span>
              <span className="hrpage__kpid">아래 «처리 대기» 참고</span>
            </div>
            <div className={`hrpage__kpi${(anomalies ?? 0) > 0 ? ' is-warn' : ''}`}>
              <span className="hrpage__kpil">근태 이상</span>
              <span className="hrpage__kpiv">
                {anomalies === null ? '—' : anomalies}{anomalies !== null && <small>건</small>}
              </span>
              <span className="hrpage__kpid">
                {today ? `미퇴근 ${today.openRecords} · 지각 ${today.late}` : '오늘 근태를 못 읽었습니다'}
              </span>
            </div>
          </div>

          <div className="hrpage__row">
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">손봐야 할 것</h2>
              <table className="hrpage__tbl">
                <tbody>
                  <tr>
                    <td>입사일 미설정 — 연차가 시작되지 않는다</td>
                    <td className="hrpage__num">{stats.missingHireDate}명</td>
                    <td className="hrpage__act"><Link to="/erp/attendance/hire-dates">입사일 관리</Link></td>
                  </tr>
                  <tr>
                    <td>연차 미산정 — 규칙이 아직 안 닿았다</td>
                    <td className="hrpage__num">{stats.quota.missing}명</td>
                    <td className="hrpage__act"><Link to="/erp/attendance/leave">연차 관리</Link></td>
                  </tr>
                  <tr>
                    <td>소속 팀 미배정 — 결재 사다리의 팀장 칸이 빈다</td>
                    <td className="hrpage__num">{stats.missingTeam}명</td>
                    <td className="hrpage__act"><Link to="/erp/hr/users">사용자 관리</Link></td>
                  </tr>
                  <tr>
                    {/* ★ 예전에는 «여기서는 세지 않는다» 였다 — 이제 전사 한 장을 받아 센다. */}
                    <td>미퇴근 — 출근만 찍고 퇴근이 없다 (최근 7일)</td>
                    <td className="hrpage__num">{today ? `${today.openRecords}건` : '—'}</td>
                    <td className="hrpage__act"><Link to="/erp/attendance/company">근태 현황</Link></td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                이번 달 입사
                <span className="hrpage__panels">{stats.hiredThisMonth.length}명</span>
              </h2>
              {stats.hiredThisMonth.length === 0 ? (
                <p className="hrpage__empty">이번 달 입사자가 없습니다.</p>
              ) : (
                <table className="hrpage__tbl">
                  <tbody>
                    {stats.hiredThisMonth.slice(0, 6).map((e) => (
                      <tr key={e.userId}>
                        <td><b>{e.name}</b> <span className="hrpage__dim">{e.teamName ?? '회사 직속'}</span></td>
                        <td className="hrpage__num">{e.hireDate}</td>
                        <td className="hrpage__act">
                          {e.annualDays !== null ? `연차 ${e.annualDays}일` : '연차 미산정'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          {/* ── 오늘 근태 · 월별 입 · 퇴사 (시안 A1) ─────────────────────────
              ★ 도넛은 **나온 사람 비율**이다 — 휴가와 미출근만 뺀다. 숫자만 받으므로
                누가 언제인지는 여기 없다(그건 «근태 현황» 의 일이다). */}
          <div className="hrpage__row">
            {today && (
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  오늘 근태
                  <span className="hrpage__panels">{today.date} · 재직 {today.total}명</span>
                </h2>
                <div className="hrhome__today">
                  <div className="hrhome__donut" role="img" aria-label={`나온 사람 ${today.rate}%`}>
                    <svg viewBox="0 0 42 42" aria-hidden>
                      <circle className="hrhome__donutbg" cx="21" cy="21" r="15.9155" />
                      <circle
                        className="hrhome__donutfg" cx="21" cy="21" r="15.9155"
                        strokeDasharray={`${today.rate} ${100 - today.rate}`} strokeDashoffset="25"
                      />
                    </svg>
                    <span className="hrhome__donutv">{today.rate}<small>%</small></span>
                  </div>
                  <table className="hrpage__tbl hrhome__todaytbl">
                    <tbody>
                      <tr><td>정상 출근</td><td className="hrpage__num">{today.working + today.off}명</td></tr>
                      <tr><td>출장 · 외근</td><td className="hrpage__num">{today.trip}명</td></tr>
                      <tr><td>휴가</td><td className="hrpage__num">{today.onLeave}명</td></tr>
                      <tr className={today.absent > 0 ? 'hrhome__warnrow' : ''}>
                        <td>미출근</td><td className="hrpage__num">{today.absent}명</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                월별 입 · 퇴사
                <span className="hrpage__panels">{year}년</span>
              </h2>
              {/* ★ 눈금 없이 **서로 견주는** 그림이다 — 가장 큰 달을 100 으로 잡는다.
                  값은 막대에 붙는 설명으로도 읽히게 한다(그림만으로는 못 읽는 사람이 있다). */}
              <div className="hrhome__flow">
                {flow.map((m) => (
                  <div className="hrhome__flowm" key={m.month}>
                    <span className="hrhome__flowbars">
                      <i
                        className="hrhome__bar hrhome__bar--in"
                        style={{ height: `${Math.round((m.hired / flowMax) * 100)}%` }}
                        title={`${m.month}월 입사 ${m.hired}명`}
                      />
                      <i
                        className="hrhome__bar hrhome__bar--out"
                        style={{ height: `${Math.round((m.left / flowMax) * 100)}%` }}
                        title={`${m.month}월 퇴사 ${m.left}명`}
                      />
                    </span>
                    <span className="hrhome__flowl">{m.month}</span>
                  </div>
                ))}
              </div>
              <p className="hrhome__legend">
                <span className="hrhome__key hrhome__key--in" aria-hidden /> 입사
                <span className="hrhome__key hrhome__key--out" aria-hidden /> 퇴사
              </p>
            </section>
          </div>

          {stats.teamUsage.length > 0 && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                팀별 연차 소진율
                <span className="hrpage__panels">덜 쓴 팀이 먼저 옵니다 — 촉진 대상</span>
              </h2>
              <table className="hrpage__tbl">
                <tbody>
                  {stats.teamUsage.slice(0, 6).map((t) => (
                    <tr key={t.team}>
                      <td>{t.team}</td>
                      <td className="hrpage__bar">
                        <span><i style={{ width: `${Math.min(100, t.rate)}%` }} /></span>
                      </td>
                      <td className="hrpage__num">{t.rate}%</td>
                      <td className="hrpage__act">{t.used} / {t.total}일</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {state === 'folded' && (
        <div className="hrpage__note">
          지금 현황을 불러오지 못했습니다 — 아래 화면으로는 그대로 들어갈 수 있습니다.
        </div>
      )}

        <section className="hrpage__panel">
          <h2 className="hrpage__panelt">
            처리 대기
            <span className="hrpage__panels">
              {pending ? pendingHeadline(pending) : '읽는 중…'}
            </span>
          </h2>
          {!pending ? (
            <p className="hrpage__empty">불러오는 중…</p>
          ) : (
            <table className="hrpage__tbl">
              <tbody>
                {pending.map((r) => (
                  <tr key={r.key} className={r.urgent ? 'is-overdue' : ''}>
                    <td className={r.urgent ? '' : 'hrpage__dim'}>
                      {r.label}
                      <span className="hrpage__dim hrhome__why"> — {r.why}</span>
                      {r.extra && <div className="hrpage__dim hrhome__why">{r.extra}</div>}
                    </td>
                    <td className={`hrpage__num${r.urgent ? ' hrhome__hot' : ' hrpage__dim'}`}>
                      {countText(r.count, r.unit)}
                    </td>
                    <td className="hrpage__act"><Link to={r.to}>{r.linkLabel}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

      <section className="hrpage__panel">
        <h2 className="hrpage__panelt">
          최근 인사 이벤트
          <span className="hrpage__panels">적용된 발령 · 입사</span>
        </h2>
        {/* ★ 예정은 안 온다 — 아직 일어나지 않은 일은 «처리 대기» 의 몫이다. */}
        {events.length === 0 ? (
          <p className="hrpage__empty">최근에 적용된 인사 기록이 없습니다.</p>
        ) : (
          <table className="hrpage__tbl">
            <tbody>
              {events.map((e) => (
                <tr key={e.key}>
                  <td className="hrpage__num hrpage__dim">{e.date.slice(5)}</td>
                  <td><b>{e.name}</b> <span className="hrpage__pill">{e.kind}</span></td>
                  <td className="hrpage__dim">{e.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/*
        ★ 「바로가기」 절을 걷어냈다 (2026-08-29 · 사용자 지적).
          왼쪽에 트리가 서 있는데 같은 목록을 카드로 한 번 더 그리면, 홈에서 할 수 있는 일이
          «메뉴를 다시 보여 주는 것» 뿐이 된다. 홈은 **오늘 처리할 것**만 이고 있으면 된다.
      */}
    </div>
  );
}
